// ---------------------------------------------------------------------------
// The rules' tunable numbers, in one place — and the rules version
// ---------------------------------------------------------------------------
//
// Every number a designer might tune lives in DEFAULT_RULES. The rules read
// them through rules() at call time, so a caller can run the same code under
// another variant: resolveRound, submitOrders and the simulation take an
// optional `config` override (withRules applies it for the length of one
// synchronous call — the rules never await, so nothing else can observe it).
// gameConfig.js re-exports the defaults under their old constant names for
// the app, which always plays DEFAULT_RULES.
//
// RULES_VERSION names the behaviour of the rules: bump it on ANY change that
// alters resolveRound's output (a rule edit or a new default here). Every new
// war is stamped with it, and so is the simulator's output. The golden
// snapshot tests hold it too: they fail until the version is bumped and the
// snapshots regenerated (npm run rules:update-golden).

import { DEFAULT_PROGRESSION } from './progression/config.js'

export const RULES_VERSION = 10

// The economy's numbers come from the pacing sheet (progression/pacing/
// inputs.csv); a war snapshots them into its own config when it starts
// (progression/warSetup.js), so retuning the sheet never changes a war
// already running.
const SHEET = DEFAULT_PROGRESSION.inputs

export const DEFAULT_RULES = Object.freeze({
    // Units
    armiesPerRound: 2,             // fresh drones each player deploys per round
    capacityMin: 2,                // a corridor's capacity is drawn from [capacityMin, capacityMax]
    capacityMax: 5,
    // Attacks
    firstAttackRound: 3,           // no corridor can attack before this round (a new war's boards hold it)
    // Broods
    fortStrength: 2,               // a mature brood's battle worth (one per live drone)
    fortInConstructionStrength: 0, // a brood still under construction fights with this
    fortMaturationRounds: 2,       // rounds of attacks a construction must survive to mature
    broodCost: 2,                  // veterans (that started the round in the corridor) a brood spends
    dropZonesPerBase: 1,           // airdrops a laying brood earns per round
    // Economy
    startingYield: 0,              // each player's yield when the war starts
    yieldPerLotPerRound: SHEET.yieldPerLotPerRound,           // each producing lot, per round
    extractorYieldMultiplier: SHEET.extractorYieldMultiplier, // a network its extractor stands on
    rocketCost: SHEET.rocketCostYield,
    droneCost: SHEET.reconCostYield,  // one recon plane
    // Grid defence and intel
    rocketCoverageSteps: 2,        // a rocket covers every cell within this many hex steps
    bearingRange: null,            // distance intel reaches this far; null = unlimited
    // The Grid
    homefrontMinStructures: 7,
    homefrontMaxStructures: 7,
    homefrontMaxPerShape: 2,
    // Winning (a war sets its own: the lower of the two arenas' kills to win)
    structuresToWin: 3,            // a player who has lost this many structures has lost the war
})

const KEYS = new Set(Object.keys(DEFAULT_RULES))

// A full rules object from an override: unknown keys and non-numbers are
// refused loudly (a typo in a variant file must not silently run defaults).
export function resolveRules(override = null) {
    if (override == null) return DEFAULT_RULES
    const out = { ...DEFAULT_RULES }
    for (const [k, v] of Object.entries(override)) {
        if (!KEYS.has(k)) throw new Error(`Unknown rules config key "${k}"`)
        if (v !== null && (typeof v !== 'number' || Number.isNaN(v))) throw new Error(`Rules config "${k}" must be a number${k === 'bearingRange' ? ' or null' : ''}`)
        if (v === null && k !== 'bearingRange') throw new Error(`Rules config "${k}" must be a number`)
        out[k] = v
    }
    if (out.capacityMin > out.capacityMax) throw new Error('capacityMin must not exceed capacityMax')
    return Object.freeze(out)
}

let active = DEFAULT_RULES

// The rules in force for the current call.
export function rules() {
    return active
}

// Runs fn with `override` in force, then restores what was there before.
export function withRules(override, fn) {
    if (override == null) return fn()
    const prev = active
    active = resolveRules(override)
    try {
        return fn()
    } finally {
        active = prev
    }
}
